import React from 'react';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';
import { Booking, BusinessSettings } from '../types';
import { getBusinessSettings } from '../utils/businessSettings';

interface InvoicePDFProps {
  booking: Booking;
  businessSettings?: BusinessSettings;
  id?: string;
}

export function InvoicePDFTemplate({ booking, businessSettings, id = 'invoice-pdf-template' }: InvoicePDFProps) {
  const settings = businessSettings || getBusinessSettings();
  const invNumber = booking.invoice_number || `INV-${booking.id}`;
  const clientName = booking.client_name || 'Valued Client';
  const issueDate = booking.date ? new Date(booking.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A';
  const dueDate = booking.due_date ? new Date(booking.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Upon Receipt';
  const status = booking.status === 'paid' ? 'PAID' : booking.status === 'cancelled' ? 'CANCELLED' : 'UNPAID';
  const subtotal = booking.price;
  const currencySym = settings.currencySymbol || 'R';
  const vatRate = typeof settings.vatRate === 'number' ? settings.vatRate : 15;
  const vatAmount = subtotal * (vatRate / 100);
  const total = subtotal + vatAmount;
  const invoiceLabel = settings.invoiceTypeLabel || 'INVOICE';

  const senderCompany = settings.companyName || settings.senderName || 'Your Business Name';
  const senderName = settings.companyName ? settings.senderName : '';

  return (
    <div
      id={id}
      style={{
        width: '800px',
        backgroundColor: '#ffffff',
        color: '#111827',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '40px',
        boxSizing: 'border-box',
        border: '1px solid #e5e7eb',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '24px',
          marginBottom: '24px',
        }}
      >
        <div>
          {settings.logoUrl ? (
            <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={settings.logoUrl}
                alt={senderCompany}
                crossOrigin="anonymous"
                style={{ maxHeight: '48px', maxWidth: '180px', objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', letterSpacing: '-0.025em' }}>
                {senderCompany}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: '#14B8A6',
                  color: '#000000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '18px',
                }}
              >
                S
              </div>
              <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#111827', letterSpacing: '-0.025em' }}>
                {senderCompany}
              </span>
            </div>
          )}

          {senderName && (
            <p style={{ fontSize: '12px', color: '#374151', fontWeight: '600', margin: '2px 0' }}>
              Attn: {senderName}
            </p>
          )}
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>{settings.email}</p>
          {settings.phone && <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>{settings.phone}</p>}
          {settings.address && <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0' }}>{settings.address}</p>}
          {settings.taxNumber && <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0' }}>Tax / VAT ID: {settings.taxNumber}</p>}
          {settings.registrationNumber && <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0' }}>Company Reg: {settings.registrationNumber}</p>}
        </div>

        <div style={{ textAlign: 'right' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
            {invoiceLabel}
          </h1>
          <p style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: '600', color: '#0d9488', marginTop: '4px', margin: '4px 0 0 0' }}>
            {invNumber}
          </p>
          <div style={{ marginTop: '12px', display: 'inline-block' }}>
            <span
              style={{
                fontSize: '12px',
                fontWeight: 'bold',
                padding: '4px 12px',
                borderRadius: '9999px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                backgroundColor: status === 'PAID' ? '#d1fae5' : status === 'CANCELLED' ? '#fee2e2' : '#fef3c7',
                color: status === 'PAID' ? '#065f46' : status === 'CANCELLED' ? '#991b1b' : '#92400e',
                border: status === 'PAID' ? '1px solid #a7f3d0' : status === 'CANCELLED' ? '1px solid #fecaca' : '1px solid #fde68a',
              }}
            >
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* Invoice Details Grid: FROM & TO */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
        <div>
          <h3 style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0d9488', margin: '0 0 6px 0' }}>
            FROM (ISSUER)
          </h3>
          <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{senderCompany}</p>
          {senderName && <p style={{ fontSize: '12px', color: '#4b5563', margin: '2px 0 0 0' }}>{senderName}</p>}
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>{settings.email}</p>
          {settings.phone && <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>{settings.phone}</p>}
          {settings.address && <p style={{ fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0' }}>{settings.address}</p>}
        </div>

        <div>
          <h3 style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0d9488', margin: '0 0 6px 0' }}>
            TO (BILLED TO)
          </h3>
          <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{clientName}</p>
          {booking.client_company && (
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#374151', margin: '2px 0 0 0' }}>{booking.client_company}</p>
          )}
          {booking.client_email && (
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>{booking.client_email}</p>
          )}
          {booking.client_phone && (
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0 0' }}>{booking.client_phone}</p>
          )}
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0 0' }}>Client Ref: {booking.id}</p>
        </div>
      </div>

      {/* Dates Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '24px', paddingBottom: '12px', borderBottom: '1px solid #f3f4f6' }}>
        <div>
          <span style={{ color: '#9ca3af', fontWeight: '500' }}>Issue Date: </span>
          <span style={{ color: '#1f2937', fontWeight: '600' }}>{issueDate}</span>
        </div>
        <div>
          <span style={{ color: '#9ca3af', fontWeight: '500' }}>Payment Due: </span>
          <span style={{ color: '#1f2937', fontWeight: '600' }}>{dueDate}</span>
        </div>
      </div>

      {/* Line Items Table */}
      <div style={{ marginBottom: '32px' }}>
        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #d1d5db', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
              <th style={{ padding: '12px 8px' }}>Description / Service</th>
              <th style={{ padding: '12px 8px', textAlign: 'center' }}>Category</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '14px' }}>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '16px 8px', fontWeight: '500', color: '#111827' }}>
                {booking.client_name ? `${booking.type} - Service for ${booking.client_name}` : booking.type}
              </td>
              <td style={{ padding: '16px 8px', textAlign: 'center', fontWeight: '500', textTransform: 'capitalize', color: '#4b5563' }}>
                {booking.type}
              </td>
              <td style={{ padding: '16px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: '600', color: '#111827' }}>
                {currencySym} {subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary Box */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '48px' }}>
        <div style={{ width: '256px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
            <span>Subtotal</span>
            <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>
              {currencySym} {subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
            <span>VAT / Tax ({vatRate}%)</span>
            <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>
              {currencySym} {vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#111827',
              borderTop: '1px solid #d1d5db',
              paddingTop: '12px',
              marginTop: '12px',
            }}
          >
            <span>Total Due (incl. Tax)</span>
            <span style={{ fontFamily: 'monospace', color: '#0d9488' }}>
              {currencySym} {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Footer / Payment Instructions */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px', fontSize: '12px', color: '#6b7280' }}>
        <p style={{ fontWeight: 'bold', color: '#0d9488', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>
          Payment Methods & Instructions
        </p>

        {settings.paymentLink && (
          <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
            <p style={{ fontWeight: 'bold', color: '#166534', margin: '0 0 4px 0', fontSize: '12px' }}>
              💳 Pay Online Directly (Card / Gateway):
            </p>
            <a
              href={settings.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '600', wordBreak: 'break-all' }}
            >
              {settings.paymentLink}
            </a>
          </div>
        )}

        {settings.bankDetails && (
          <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <p style={{ fontWeight: 'bold', color: '#374151', margin: '0 0 4px 0', fontSize: '12px' }}>
              🏦 Direct Bank Transfer / EFT Details:
            </p>
            <p style={{ margin: 0, color: '#4b5563', fontFamily: 'monospace', fontSize: '11px' }}>
              {settings.bankDetails}
            </p>
          </div>
        )}

        <p style={{ margin: '0 0 4px 0' }}>
          Please use invoice number{' '}
          <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#111827' }}>{invNumber}</span> as the payment reference.
        </p>
        <p style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', margin: '8px 0 0 0' }}>
          Generated electronically via AzuraOS • Thank you for your business!
        </p>
      </div>
    </div>
  );
}

export async function generateInvoicePDF(booking: Booking, elementId = 'invoice-pdf-template'): Promise<boolean> {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Invoice template element #${elementId} not found`);
      return false;
    }

    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas-pro')
    ]);

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      onclone: (clonedDoc) => {
        sanitizeClonedDocForPdf(clonedDoc);
      },
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, Math.min(imgHeight, pageHeight));

    const sanitizedClient = (booking.client_name || 'Client').replace(/[^a-z0-9]/gi, '_');
    const invNum = booking.invoice_number || `INV-${booking.id}`;
    const fileName = `${invNum}_${sanitizedClient}.pdf`;

    pdf.save(fileName);
    return true;
  } catch (err) {
    console.error('Failed to generate PDF:', err);
    return false;
  }
}

